import api from './api';

/**
 * Admin-Service: Pro-Turniere + KI-Daten-Sync.
 *
 * Turnier-CRUD: /api/admin/pro-tournaments (bestehende Backend-API)
 * Sync (Quellen/Settings/Läufe): /api/admin/pro-tournament-sync/*
 * 4xx-Fehler kommen als Plaintext (getErrorMessage nutzen).
 */

export interface ProTournamentAdmin {
  _id?: string | null;
  id?: string | null;
  slug?: string | null;
  name?: string | null;
  shortName?: string | null;
  edition?: string | null;
  year?: number | null;
  organization?: string | null;
  tier?: string | null;
  status?: string | null;
  visible?: boolean | null;
  featured?: boolean | null;
  startDate?: number | null;
  endDate?: number | null;
  dateLabel?: string | null;
  venue?: string | null;
  city?: string | null;
  country?: string | null;
  prizeMoneyLabel?: string | null;
  thumbnail?: string | null;
  heroImage?: string | null;
  intro?: string | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
  createdAt?: number | null;
}

export interface ProTournamentSyncSource {
  id?: string | null;
  tournamentInfoId?: string | null;
  name?: string | null;
  url?: string | null;
  enabled?: boolean | null;
  lastCheckedAt?: number | null;
  lastError?: string | null;
  createdAt?: number | null;
}

export interface ProTournamentSyncSettings {
  enabled?: boolean | null;
  intervalMinutes?: number | null;
  claudeModel?: string | null;
  aiBanners?: boolean | null;
  lastRunAt?: string | number | null;
}

export interface ProTournamentSyncRunResult {
  tournamentId?: string | null;
  slug?: string | null;
  name?: string | null;
  sourcesUsed?: number | null;
  updatedFields?: string[] | null;
  bannerGenerated?: boolean | null;
  error?: string | null;
}

export interface ProTournamentSyncRun {
  id?: string | null;
  startedAt?: number | null;
  finishedAt?: number | null;
  status?: 'running' | 'success' | 'error' | string | null;
  triggeredBy?: string | null;
  tournamentsProcessed?: number | null;
  results?: ProTournamentSyncRunResult[] | null;
  errors?: string[] | null;
}

class AdminProTournamentsService {
  // ---------- Turniere ----------

  async listTournaments(): Promise<ProTournamentAdmin[]> {
    const response = await api.get('/api/admin/pro-tournaments', { params: { limit: 100 } });
    return (response.data?.tournaments as ProTournamentAdmin[]) ?? [];
  }

  async createTournament(payload: { name: string; organization?: string }): Promise<ProTournamentAdmin> {
    const response = await api.post('/api/admin/pro-tournaments', {
      name: payload.name,
      organization: payload.organization || 'PDC',
    });
    return response.data as ProTournamentAdmin;
  }

  async updateTournament(id: string, payload: Partial<ProTournamentAdmin>): Promise<ProTournamentAdmin> {
    const response = await api.put(`/api/admin/pro-tournament/id/${id}`, payload);
    return response.data as ProTournamentAdmin;
  }

  async deleteTournament(id: string): Promise<void> {
    await api.delete(`/api/admin/pro-tournament/id/${id}`);
  }

  // ---------- Sync-Quellen ----------

  async listSources(tournamentId?: string): Promise<ProTournamentSyncSource[]> {
    const response = await api.get('/api/admin/pro-tournament-sync/sources', {
      params: tournamentId ? { tournamentId } : undefined,
    });
    return (response.data?.sources as ProTournamentSyncSource[]) ?? [];
  }

  async createSource(payload: {
    tournamentInfoId: string;
    url: string;
    name?: string;
    enabled?: boolean;
  }): Promise<ProTournamentSyncSource> {
    const response = await api.post('/api/admin/pro-tournament-sync/sources', payload);
    return (response.data?.source as ProTournamentSyncSource) ?? {};
  }

  async updateSource(id: string, payload: Partial<{
    name: string;
    url: string;
    enabled: boolean;
  }>): Promise<ProTournamentSyncSource> {
    const response = await api.put(`/api/admin/pro-tournament-sync/source/${id}`, payload);
    return (response.data?.source as ProTournamentSyncSource) ?? {};
  }

  async deleteSource(id: string): Promise<void> {
    await api.delete(`/api/admin/pro-tournament-sync/source/${id}`);
  }

  // ---------- Einstellungen ----------

  async getSettings(): Promise<ProTournamentSyncSettings> {
    const response = await api.get('/api/admin/pro-tournament-sync/settings');
    return (response.data?.settings as ProTournamentSyncSettings) ?? {};
  }

  async updateSettings(payload: Partial<{
    enabled: boolean;
    intervalMinutes: number;
    claudeModel: string;
    aiBanners: boolean;
  }>): Promise<ProTournamentSyncSettings> {
    const response = await api.put('/api/admin/pro-tournament-sync/settings', payload);
    return (response.data?.settings as ProTournamentSyncSettings) ?? {};
  }

  // ---------- Läufe ----------

  /** Lauf starten – optional nur für EIN Turnier (tournamentId). 409 wenn schon aktiv. */
  async triggerRun(tournamentId?: string): Promise<void> {
    await api.post('/api/admin/pro-tournament-sync/run', tournamentId ? { tournamentId } : {});
  }

  async listRuns(limit = 20): Promise<{ runs: ProTournamentSyncRun[]; total: number }> {
    const response = await api.get('/api/admin/pro-tournament-sync/runs', { params: { limit } });
    return {
      runs: (response.data?.runs as ProTournamentSyncRun[]) ?? [],
      total: response.data?.total ?? 0,
    };
  }

  // ---------- Banner ----------

  /**
   * Lädt ein Bild hoch (POST /api/file) und liefert die /api/file/-URL.
   * WICHTIG: Backend erwartet ROHES Base64 (ohne data:-URL-Prefix).
   */
  async uploadBannerImage(file: File): Promise<string> {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
      reader.readAsDataURL(file);
    });
    const base64 = dataUrl.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl;
    const response = await api.post('/api/file', { data: base64 });
    const name = response.data?.name;
    if (!name) throw new Error('Upload fehlgeschlagen');
    return `/api/file/${name}`;
  }

  /** Eigenes Banner setzen (ersetzt KI-Banner). */
  async setTournamentBanner(id: string, url: string): Promise<ProTournamentAdmin> {
    return this.updateTournament(id, { thumbnail: url, heroImage: url });
  }

  /**
   * Banner entfernen. Hinweis: Ist der KI-Sync mit aiBanners aktiv,
   * generiert der nächste Lauf automatisch wieder ein KI-Banner.
   */
  async removeTournamentBanner(id: string): Promise<ProTournamentAdmin> {
    return this.updateTournament(id, { thumbnail: null, heroImage: null });
  }

  // ---------- Helper ----------

  getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { data?: unknown } };
    const data = anyErr?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    return fallback;
  }

  buildFileUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
  }
}

const adminProTournamentsService = new AdminProTournamentsService();
export default adminProTournamentsService;
